import type { AudioDoc, ChapterData } from './logic'
import { Sentry } from '@/services/sentry'
import { saveProgress } from '@/services/readingProgress'
import { completionService } from '@/services/completion'
import { playerSession } from './session'
import type { DocWithContent } from '@/models/interfaces'

/** Save chapter completion and run any guide-completion behavior it triggers. */
export async function saveAudioChapterProgress(
  doc: DocWithContent,
  completedChapterIdx: number,
): Promise<void> {
  const chapter = doc.content[completedChapterIdx]

  if (chapter.content_type !== 'chapter' || chapter.completed) {
    return
  }

  const hasCompletedDoc = await saveProgress(doc, chapter)
  if (hasCompletedDoc && doc.doc_type === 'book') {
    // Enroll the user in the guide completion experiment, if needed, and
    // register the popup for treatment users.
    const variant = await completionService.registerCompletion(doc, 'audio')
    if (variant === 'B') {
      // Pause the audio, in case the completion was triggered before
      // an exercise page (we mark the guide as completed when it ends
      // the final chapter, but there may be an exercise page after it).
      playerSession.value.pause()
    }
  }
}

/**
 * Progress bar, current time and chapter duration UI control.
 *
 * The unified logic for book and chapter progress.
 * Specific details for book and chapter are represented by
 * `BookProgress` and `ChapterProgress` accordingly.
 */
export class ChapterProgressControl {
  private _audio: AudioDoc
  private _onReadingProgress: (progressInPercent: number) => void
  private _saveCompletedChapter: typeof saveAudioChapterProgress

  // Playback position in seconds inside the current chapter.
  private _position: number = 0
  // Current chapter duration in seconds.
  private _duration: number = 0
  // Chapter progress in percents.
  private _progress: number = 0

  // We update the reading progress (the same one that is set when scrolling)
  // approximately every 3 seconds of audio listening. This variable
  // tracks the last time that we sent an update in order to avoid sending
  // one again too soon, as `timeUpdateHandler` triggers too frequently
  private _timeOfLastProgressUpdate: Date = new Date(0)

  // Used for rejecting false initial update events when updating reading %
  private _haveSeenRealProgressUpdates = false

  // The last chapter that was listened to. We use this when determining if
  // this chapter ended and should be marked as completed.
  private _lastListenedChapter?: ChapterData

  // The chapter we have already sent the completion request for. The last
  // chapter keeps reporting itself as ended while the audio sits at the end
  // of the file, and `completed` is only set once the backend responds.
  private _completedChapterIdx?: number

  public _updateCallback: any = null

  public _hasMetadata: boolean = false

  constructor(
    audio: AudioDoc,
    onReadingProgress: (progressInPercent: number) => void,
    saveCompletedChapter: typeof saveAudioChapterProgress = saveAudioChapterProgress,
  ) {
    this._audio = audio
    this._onReadingProgress = onReadingProgress
    this._saveCompletedChapter = saveCompletedChapter
  }

  public initEventListeners(): void {
    const eventHandler = (): void => {
      this._timeUpdateHandler()
    }

    this._audio.addListener('timeupdate', eventHandler)
    // The loadedmetadata handler is needed to update audio file duration
    // when it becomes known.
    this._audio.addListener('loadedmetadata', () => {
      this._hasMetadata = true
      // Event listeners can observe metadata before chapter navigation catches
      // up with the current time. Updating the stale chapter could mark it as
      // completed; the normal timeupdate after navigation recalculates it.
      if (this._audio.currentTime <= this._audio.chapters.current().end) {
        this._timeUpdateHandler()
      }
    })

    // Update durations on playback rate change.
    this._audio.addListener('ratechange', eventHandler)
  }

  /**
   * Handle audio `timeupdate' event.
   *
   * This is used to track and display current playback position
   * (text value, book and chapter progress bars).
   */
  _timeUpdateHandler(): void {
    this._updateProgress()
  }

  /**
   * Playback position within current chapter, seconds.
   */
  get position(): string {
    if (this._hasMetadata) {
      return this._formatTime(this._position)
    }
    return '0:00'
  }

  /**
   * Current chapter position as number.
   */
  get positionInPercent(): number {
    return Math.round((100 / this.durationInSeconds) * this._position)
  }

  /**
   * Position in the whole audio as a percentage.
   *
   * `positionInPercent` only covers the chapter that is playing, this one
   * covers the whole audio file.
   */
  get audioPositionPercent(): number {
    // `_position` is relative to the chapter start and scaled by the playback
    // rate, undo both to get the position in the audio file.
    const position =
      this._position * this._audio.playbackRate +
      this._audio.chapters.current().start

    const duration = this._audio.duration
    if (!duration) {
      return 0
    }

    return Math.round((100 / duration) * position)
  }

  /**
   * Current chapter duration, seconds.
   */
  get duration(): string {
    if (this._hasMetadata) {
      return this._formatTime(this._duration)
    }
    return '0:00'
  }

  /**
   * Current chapter duration as number.
   */
  get durationInSeconds(): number {
    return this._duration
  }

  /**
   * Handles the upated progress bar/slider click/drag.
   *
   * We get the click position inside the `progressBar` and
   * then set the audio position accordingly.
   */
  inputChange(percent: number): void {
    const seconds = percent * (this._duration / 100) * this._audio.playbackRate
    this._audio.currentTime = this._audio.chapters.current().start + seconds
    this._updateProgress()
  }

  /**
   * Update current position, duration and progress value.
   *
   * Called in response to the `timeupdate` audio event and from
   * the `seek` method, when we click the progress bar.
   */
  _updateProgress(): void {
    // We call chapters.update in ChapterPrevNextControl,
    // it also has the "timeupdate" event handler.
    // this._chapters.update(this._audio.currentTime)

    // Because of a WebKit bug (see https://github.com/shortformhq/main/issues/5415#issuecomment-1127806210),
    // we can end up in a situation where this._calculatePosition() is a negative value.
    // We don't want that, so we cap the lower bound at 0.
    this._position = Math.max(
      0,
      this._calculatePosition() / this._audio.playbackRate,
    )
    this._duration = this._calculateDuration() / this._audio.playbackRate

    if (this._audio.duration) {
      this._progress = (this._position / this._duration) * 100
    }

    const newlyCompletedChapter = this._getNewCompletedChapter()
    if (newlyCompletedChapter !== undefined) {
      this._updateDocProgress(newlyCompletedChapter)
    }

    this._readingProgressCallback()

    if (this._updateCallback) {
      this._updateCallback()
    }
  }

  /**
   * Call the `_onReadingProgress` callback if enough time has passed since the last time we called it.
   */
  _readingProgressCallback(): void {
    if (!this._haveSeenRealProgressUpdates && this._progress < 0.0001) {
      // Because `this._progress` is initialised to 0, we often get 1 to 3
      // spurious initial updates of 0% when the audio player component mounts.
      //
      // If the audio is actually already in progress that will be quickly
      // corrected in the next tick.
      //
      // If the reader is truly on 0% progress, it doesn't matter, that's
      // the default, so no need to update the reading progress.
      //
      // On the other hand if the audio is actually in progress (or reading
      // progress exists from the page scroll position), we don't want to
      // incorrectly set reading progress to 0.
      //
      // Therefore we just ignore updates matching that pattern.
      return
    }
    this._haveSeenRealProgressUpdates = true
    const now = new Date()
    const threeSecondsAgo = new Date(now.getTime() - 3000)
    const needToUpdate = threeSecondsAgo > this._timeOfLastProgressUpdate
    if (needToUpdate) {
      this._timeOfLastProgressUpdate = now
      this._onReadingProgress(Math.round(this._progress))
    }
  }

  /**
   * Update the doc's progress to mark the given `completedChapterIdx` as completed.
   */
  async _updateDocProgress(completedChapterIdx: number): Promise<void> {
    const doc = this._audio.doc
    const chapter = doc.content[completedChapterIdx]

    // The audio timings cover every page, and pages include exercises, which
    // don't count towards progress. We also don't need to send a new request
    // if the chapter is already marked as completed.
    if (
      chapter.content_type !== 'chapter' ||
      chapter.completed ||
      this._completedChapterIdx === completedChapterIdx
    ) {
      return
    }
    this._completedChapterIdx = completedChapterIdx

    try {
      await this._saveCompletedChapter(doc, completedChapterIdx)
    } catch (error) {
      // Clear the retry flag for the failed completion request.
      // A later chapter may start saving while this request is running
      // so we release the flag only if it's the one that we expected. This
      // allows a later update to retry the completion request.
      if (this._completedChapterIdx === completedChapterIdx) {
        this._completedChapterIdx = undefined
      }
      Sentry.captureException(error)
    }
  }

  /**
   * Check if the current chapter has ended, and if so, return its index.
   *
   * This method is called on every `timeupdate` event, but by the time it's
   * called, `ChapterPrevNextControl` has already advanced to the next chapter
   * if the chapter has ended.
   *
   * We consider the chapter to have ended if the current chapter is the next one
   * in the document order after the last listened chapter (ie. we reached this chapter
   * by navigating from the last listened chapter when it ended). We could technically
   * just check if the previous chapter has ended and is not completed, but that would
   * incorrectly mark a chapter as completed if the user manually navigates to a chapter
   * without listening to the previous one (eg. the user was in chapter 1, then navigated
   * to chapter 3. With that logic, we would mark chapter 2 as completed, even though it
   * was not listened to).
   */
  _getNewCompletedChapter(): number | undefined {
    const currentChapter = this._audio.chapters.current()
    const previousChapter = this._lastListenedChapter

    // Update the last listened chapter to the current one for the next time this method is called.
    this._lastListenedChapter = currentChapter

    // If the current chapter is the next one in order after the previously listened chapter,
    // it means the previous chapter has ended, and the player navigated to the next chapter,
    // so we must mark the previous chapter as completed.
    if (previousChapter && currentChapter.idx === previousChapter.idx + 1) {
      return previousChapter.idx
    }

    // If the current chapter is the last one and the audio has completed,
    // there's no next chapter to navigate to, so we mark the current chapter as completed,
    // not the previous one.
    const isLastChapter = !this._audio.chapters.hasNext()
    const isCompleted = this._position >= this._duration
    if (isLastChapter && isCompleted) {
      return currentChapter.idx
    }

    // Otherwise, there's no new completed chapter to mark.
    return undefined
  }

  /**
   * Playback position in seconds for the current chapter.
   *
   * We calculate it as audio `currentTime` minus chapter start time.
   * So if audio current time is 3 minutes and chapter starts at 2-nd minute,
   * we will have 1 minute progress inside the chapter.
   */
  _calculatePosition(): number {
    if (this._audio.currentTime > this._audio.chapters.current().end) {
      this._audio.currentTime = this._audio.chapters.current().end
    }
    if (this._audio.currentTime === 0) {
      // We set it in the `ChapterPrevNextControl._canplayHandler` - we will either restore the
      // audio position to some place inside the chapter or set it to the start of the chapter.
      // While audio time is not set yet, we return 0 here; otherwise, we get a negative value for
      // a brief time while audio is loading and player is initializing.
      return 0
    }
    return this._audio.currentTime - this._audio.chapters.current().start
  }

  /**
   * Current chapter duration in seconds.
   */
  _calculateDuration(): number {
    let end = this._audio.chapters.current().end
    if (end === Infinity) {
      // The audio duration is not avaialble when we initialize chapters,
      // so the `end` for the last chapter is set to Infinity
      // (it is OK for comparisons where we dectect which chapter is playing now,
      // but not good to calculate the chapter duration).
      end = this._audio.duration
    }
    return end - this._audio.chapters.current().start
  }

  /**
   * Format time from seconds to HH:MM:SS format.
   */
  _formatTime(seconds: number): string {
    const hours = Math.floor(seconds / (60 * 60))
    const reminder = seconds % (60 * 60)

    const minutes = Math.floor(reminder / 60)
    const sec = Math.floor(reminder % 60)
    const secStr = sec > 9 ? sec.toString() : '0' + sec.toString()

    if (hours) {
      return `${hours}:${minutes}:${secStr}`
    }
    return `${minutes}:${secStr}`
  }
}
